import React, { useState } from 'react';
import { CategoryDynamicComponent } from '../../types/dynamicComponents';
import { parseSquaresTableConfig } from './definitions';

interface DynamicSquaresTableProps {
  component: CategoryDynamicComponent;
  selectedNumber: number | undefined;
  onSelectionChange: (componentId: number, value: number) => void;
}

const DynamicSquaresTable: React.FC<DynamicSquaresTableProps> = ({
  component,
}) => {
  const [activeMode, setActiveMode] = useState<'squares' | 'cubes' | 'square_root' | 'primes'>('squares');
  const introHtmlValue = component.introHtml?.trim();
  const captionOverrideValue = component.captionOverride?.trim();
  const tableConfig = parseSquaresTableConfig(component.configJson);

  const numberButtons = Array.from(
    { length: tableConfig.maxNumber - tableConfig.minNumber + 1 },
    (_, index) => tableConfig.minNumber + index
  );

  const isPrime = (n: number): boolean => {
    if (n < 2) return false;
    for (let i = 2; i <= Math.sqrt(n); i++) {
      if (n % i === 0) return false;
    }
    return true;
  };

  const squareRows = numberButtons;
  const primeRows = numberButtons.filter(isPrime);
  const activeRows = activeMode === 'primes' ? primeRows : squareRows;
  const midpoint = Math.ceil(activeRows.length / 2);
  const leftColumnRows = activeRows.slice(0, midpoint);
  const rightColumnRows = activeRows.slice(midpoint);

  const getRootDisplay = (value: number): string => {
    const root = Math.sqrt(value);
    const roundedRoot = Number(root.toFixed(3));
    return `√${value} = ${roundedRoot}`;
  };

  const renderExpression = (value: number): string => {
    if (activeMode === 'cubes') {
      return `${value}³ = ${value * value * value}`;
    }

    if (activeMode === 'square_root') {
      return getRootDisplay(value);
    }

    if (activeMode === 'primes') {
      return `${value} — Prime`;
    }

    return `${value}² = ${value * value}`;
  };

  return (
    <div className="border border-emerald-200 rounded-xl bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-4 shadow-sm">
      {introHtmlValue && (
        <div
          className="html-content text-sm text-gray-700 leading-relaxed text-left mb-3"
          dangerouslySetInnerHTML={{ __html: introHtmlValue }}
        />
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          onClick={() => setActiveMode('squares')}
          className={`px-3 py-1 rounded-full border text-sm font-semibold transition-all ${
            activeMode === 'squares'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-teal-600 shadow'
              : 'bg-white text-gray-700 border-emerald-200 hover:bg-emerald-50'
          }`}
        >
          Squares
        </button>
        <button
          type="button"
          onClick={() => setActiveMode('cubes')}
          className={`px-3 py-1 rounded-full border text-sm font-semibold transition-all ${
            activeMode === 'cubes'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-teal-600 shadow'
              : 'bg-white text-gray-700 border-emerald-200 hover:bg-emerald-50'
          }`}
        >
          Cubes
        </button>
        <button
          type="button"
          onClick={() => setActiveMode('square_root')}
          className={`px-3 py-1 rounded-full border text-sm font-semibold transition-all ${
            activeMode === 'square_root'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-teal-600 shadow'
              : 'bg-white text-gray-700 border-emerald-200 hover:bg-emerald-50'
          }`}
        >
          Square Root
        </button>
        <button
          type="button"
          onClick={() => setActiveMode('primes')}
          className={`px-3 py-1 rounded-full border text-sm font-semibold transition-all ${
            activeMode === 'primes'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-teal-600 shadow'
              : 'bg-white text-gray-700 border-emerald-200 hover:bg-emerald-50'
          }`}
        >
          Prime Numbers
        </button>
      </div>

      <div className="border border-emerald-200 rounded-lg overflow-hidden bg-white">
        <div style={{ background: '#222', color: '#fff', padding: '6px 12px', fontSize: 13, fontWeight: 700, textAlign: 'center', letterSpacing: '0.03em' }}>
          {activeMode === 'squares' ? 'Squares' : activeMode === 'cubes' ? 'Cubes' : activeMode === 'square_root' ? 'Square Roots' : 'Prime Numbers'}
        </div>
        <div className="grid grid-cols-2">
        <div className="border-r border-gray-100">
          {leftColumnRows.map((value, index) => (
            <div
              key={`${component.id}-left-row-${value}`}
              className={`px-3 py-2 text-sm font-medium ${
                activeMode === 'primes'
                  ? index % 2 === 0 ? 'bg-yellow-50 text-yellow-800' : 'bg-white text-yellow-700'
                  : index % 2 === 0 ? 'bg-emerald-50 text-emerald-900' : 'bg-white text-gray-700'
              } ${
                index === leftColumnRows.length - 1 ? '' : 'border-b border-gray-100'
              }`}
            >
              {renderExpression(value)}
            </div>
          ))}
        </div>
        <div>
          {rightColumnRows.map((value, index) => (
            <div
              key={`${component.id}-right-row-${value}`}
              className={`px-3 py-2 text-sm font-medium ${
                activeMode === 'primes'
                  ? index % 2 === 0 ? 'bg-yellow-50 text-yellow-800' : 'bg-white text-yellow-700'
                  : index % 2 === 0 ? 'bg-teal-50 text-teal-900' : 'bg-white text-gray-700'
              } ${
                index === rightColumnRows.length - 1 ? '' : 'border-b border-gray-100'
              }`}
            >
              {renderExpression(value)}
            </div>
          ))}
        </div>
      </div>
      </div>

      {captionOverrideValue && <p className="mt-2 mb-0 text-xs text-gray-600 text-center">{captionOverrideValue}</p>}
    </div>
  );
};

export default DynamicSquaresTable;
